import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import { kebabCase } from 'lodash'
import isEqual from 'lodash/isEqual'
import { useNavigate } from 'react-router-dom'

import { Alert } from '@/components/ui'
import { useAuth } from '@/hooks'
import useApi from '@/hooks/useApi'
import type { SaveableAssetMapContext } from '@/pages/assets/map/MapContext'
import type {
  HistoryGraphSettings,
  ProfileType,
  SavedView,
  SavedViewPage,
  DashboardType,
  InsightConfigurationType,
  UserType,
  InsightParametersType,
  InsightCardWidth,
  InsightDefinition,
} from '@/types/dashboard-types'
import formatException from '@/utils/formatException'
import logger from '@/utils/logger'

// Note: React hot reload has issues if you put the context in the same file as the provider
import { DashboardContext, type DashboardContextType } from './DashboardContext'

const log = logger({ category: 'InsightProvider' })

const widthMapping: Record<string, InsightCardWidth> = {
  '16%': 'tiny',
  '25%': 'mini',
  '33%': 'small',
  '50%': 'medium',
  '75%': 'large',
  '100%': 'full',
}

const defaultDashboard = {
  slug: 'default',
  title: 'Default Dashboard',
  parameters: {},
  insights: [],
  isDefault: true,
}

const generateSlug = ({ slugs, title }: { slugs: string[]; title: string }) => {
  let slug = kebabCase(title)
  let idx = 0
  while (slugs.includes(slug)) {
    idx += 1
    slug = `${slug}-${idx}`
  }
  return slug
}

const DashboardProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()
  const { refreshToken } = useAuth()
  const { api, uid } = useApi()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [configuration, setConfiguration] = useState<ProfileType>({
    dashboards: [],
    units: 'Metric', // 'Metric' | 'Imperial' — case matters; consumers compare === 'Metric'
    assetMaps: {},
  })
  const [needsSync, setNeedsSync] = useState<string | boolean>()
  const [currentDashboardSlug, setCurrentDashboardSlug] = useState<string>()

  useEffect(() => {
    if (!uid || !api) {
      return
    }
    const fn = async () => {
      try {
        setIsLoading(true)
        const response = await api.get<UserType>({
          // The profile owner is resolved server-side from the auth token; the path
          // segment is ignored, so we send a static 'me' rather than leaking the
          // user's Auth0 GUID in the URL. (`uid` still gates the fetch below.)
          path: `/app/profiles/me`,
          origin: 'InsightProvider',
        })
        log.debug('app profiles response: %o', response)
        if (response.data?.profile) {
          // Should not need this, doing it on the server, but just in case we hit an old server
          // TODO: delete after we know all the profiles are converted to use dashboards instead of panels
          interface OldProfileType extends ProfileType {
            panels?: DashboardType[]
          }
          const profile = response.data.profile as OldProfileType
          if (profile.panels) {
            profile.dashboards = profile.panels
            delete profile.panels
          }
          profile.dashboards.forEach((d) => {
            if (!d.slug) {
              // Somehow someone got a slug of ''; need to figure out how but for now...
              d.slug = generateSlug({ slugs: profile.dashboards.map((d) => d.slug), title: d.title })
            }
            d.insights.forEach((i) => {
              i.width = widthMapping[i.width] || i.width
            })
          })
          // Parse assetMaps from JSON strings to objects
          if (profile.assetMaps) {
            const parsedMaps: Record<string, SaveableAssetMapContext> = {}
            for (const [name, value] of Object.entries(profile.assetMaps)) {
              try {
                parsedMaps[name] = typeof value === 'string' ? JSON.parse(value) : value
              } catch (e) {
                log.error('Failed to parse asset map %s: %o', name, e)
              }
            }
            profile.assetMaps = parsedMaps
          }
          profile.assetMaps ??= {} as Record<string, SaveableAssetMapContext>
          // savedViews arrive as JSON strings too, one map per page.
          const savedViews: NonNullable<ProfileType['savedViews']> = {}
          for (const [page, views] of Object.entries(profile.savedViews ?? {})) {
            const parsed: Record<string, SavedView> = {}
            for (const [name, value] of Object.entries(views ?? {})) {
              try {
                parsed[name] = typeof value === 'string' ? JSON.parse(value) : value
              } catch (e) {
                log.error('Failed to parse saved view %s/%s: %o', page, name, e)
              }
            }
            savedViews[page as SavedViewPage] = parsed
          }
          profile.savedViews = savedViews
          setConfiguration(profile)
        } else if (response.message === 'jwt expired') {
          refreshToken()
        } else {
          log.error('error getting profile: %o', response)
          setError('Error loading preferences. Contact support. Ref: #AI-3')
        }
      } catch (ex) {
        log.error('exception getting profile: %o', ex)
        setError(formatException(ex))
      } finally {
        setIsLoading(false)
      }
    }
    fn()
  }, [uid, api, refreshToken])

  const updateProfile = useCallback(
    async (configuration: ProfileType, slug?: string) => {
      try {
        // Only the user-settings keys go over the wire. Identity, units and the admin keys
        // (permissions, company, geotab...) are owned by the server and ignored on POST, so
        // sending them back would just be noise.
        const {
          dashboards,
          assetMaps,
          assetMapSideBarWidth,
          trackerMapListWidth,
          trackerMapColumns,
          savedViews,
          historyGraph,
        } = configuration
        const stringify = <V,>(views: Record<string, V>) =>
          Object.fromEntries(Object.entries(views).map(([name, value]) => [name, JSON.stringify(value)]))
        // Any page touched this session, even one whose last view was just deleted (sent as {} so
        // the server forgets it rather than keeping what it had).
        const hasSavedViews = Object.keys(savedViews ?? {}).length > 0
        const serializedConfig = {
          dashboards,
          // Stringify assetMaps before sending to server
          assetMaps: assetMaps ? stringify(assetMaps) : undefined,
          // Only once a page has views: an API from before savedViews rejects the key outright
          // (400), which would fail every other profile save too.
          savedViews: hasSavedViews
            ? Object.fromEntries(
                Object.entries(savedViews ?? {}).map(([page, views]) => [page, stringify(views ?? {})])
              )
            : undefined,
          assetMapSideBarWidth,
          trackerMapListWidth,
          trackerMapColumns,
          // Undefined (so left out) until the user changes the layout, for the same reason.
          historyGraph,
        }
        const response = await api.post<DashboardType, typeof serializedConfig>({
          // Owner resolved server-side from the auth token; path segment ignored.
          path: `/app/profiles/me`,
          body: serializedConfig,
          origin: 'InsightProvider',
        })
        if (response.error) {
          log.error(response)
          console.error('Backend rejected with error:', response)
          setError(response.message || '')
        } else {
          log.debug(response)
          setError('')
          if (slug) {
            navigate(`/dashboards/${slug}`)
          }
        }
      } catch (ex) {
        log.error('%o', ex)
        console.error('Backend exception:', ex)
        setError(formatException(ex))
      }
    },
    [uid, api, navigate]
  )

  useEffect(() => {
    if (!uid || !needsSync) {
      return
    }
    setNeedsSync(false)
    updateProfile(configuration)
  }, [configuration, needsSync, uid, updateProfile])

  const sync = useCallback(() => setNeedsSync(true), [])

  const addDashboard = useCallback(
    async ({
      dashboard,
      insightConfiguration,
      insights,
    }: {
      dashboard: DashboardType
      insightConfiguration?: InsightConfigurationType
      insights?: InsightConfigurationType[]
    }) => {
      const slug = generateSlug({ slugs: configuration.dashboards.map((p) => p.slug), title: dashboard.title })
      const newDashboard: DashboardType = { ...dashboard, slug, insights: insights || [] }
      if (insightConfiguration) {
        newDashboard.insights.push(insightConfiguration)
      }
      if (newDashboard.isDefault) {
        configuration.dashboards.forEach((p) => (p.isDefault = false))
      }
      // Need dashboards object reference to change so sidebar refreshes
      const newConfig = { ...configuration, dashboards: [...configuration.dashboards, newDashboard] }
      await updateProfile(newConfig, slug)
      setConfiguration(newConfig)
      return newDashboard
    },
    [configuration, updateProfile]
  )

  const setDefaultDashboard = useCallback(
    (slug: string) => {
      if (configuration.dashboards.find((d) => d.slug === slug)) {
        const newConfig = { ...configuration, dashboards: [...configuration.dashboards] }
        newConfig.dashboards.find((d) => (d.isDefault = false))
        const newDefault = newConfig.dashboards.find((d) => d.slug === slug)
        if (newDefault) {
          newDefault.isDefault = true
        }
        setConfiguration(newConfig)
        updateProfile(configuration, slug)
      }
    },
    [configuration, updateProfile]
  )

  const removeDashboard = useCallback(
    ({ slug }: { slug: string }) => {
      const newConfiguration = { ...configuration }
      newConfiguration.dashboards = newConfiguration.dashboards.filter((p) => p.slug !== slug)
      if (newConfiguration.dashboards.length === 0) {
        newConfiguration.dashboards.push(defaultDashboard) // this one has isDefault true
      } else if (!newConfiguration.dashboards.find((p) => p.isDefault)) {
        newConfiguration.dashboards[0].isDefault = true // no default, so use the first one
      }
      // we just guaranteed we have a default
      const newDefault = newConfiguration.dashboards.find((p) => p.isDefault)!.slug
      setConfiguration(newConfiguration)
      updateProfile(newConfiguration, newDefault)
    },
    [configuration, updateProfile]
  )

  const reorderDashboards = useCallback(
    (dashboards: DashboardType[]) => {
      const newConfiguration = { ...configuration, dashboards }
      setConfiguration(newConfiguration)
      setNeedsSync(true)
    },
    [configuration]
  )

  const updateDashboardParameters = useCallback(
    (parameters: InsightParametersType) => {
      setConfiguration((previous) => {
        const dashboards = [...(previous?.dashboards || [])]
        const dashboardIdx = dashboards.findIndex((p) => p.slug === currentDashboardSlug)
        if (dashboardIdx < 0) {
          log.error('updateDashboardParameters: no dashboard for %s', currentDashboardSlug)
          return previous
        }
        const updatedDashboard = { ...dashboards[dashboardIdx], parameters }
        const updatedDashboards = [...dashboards]
        updatedDashboards[dashboardIdx] = updatedDashboard
        return { ...previous, dashboards: updatedDashboards }
      })
      setNeedsSync(true)
    },
    [currentDashboardSlug]
  )

  // Functional updates, so several calls in one event (un-defaulting one view while saving
  // another) all land instead of each overwriting the last from the same stale configuration.
  const saveAssetMap = useCallback((assetMap: SaveableAssetMapContext) => {
    setConfiguration((previous) => ({
      ...previous,
      assetMaps: {
        ...previous.assetMaps,
        [assetMap.name]: assetMap,
      },
    }))
    setNeedsSync(true)
  }, [])

  const deleteAssetMap = useCallback((name: string) => {
    setConfiguration((previous) => {
      const newAssetMaps = { ...previous.assetMaps }
      delete newAssetMaps[name]
      return { ...previous, assetMaps: newAssetMaps }
    })
    setNeedsSync(true)
  }, [])

  const saveView = useCallback((page: SavedViewPage, view: SavedView) => {
    setConfiguration((previous) => ({
      ...previous,
      savedViews: { ...previous.savedViews, [page]: { ...previous.savedViews?.[page], [view.name]: view } },
    }))
    setNeedsSync(true)
  }, [])

  const deleteView = useCallback((page: SavedViewPage, name: string) => {
    setConfiguration((previous) => {
      const views = { ...previous.savedViews?.[page] }
      delete views[name]
      return { ...previous, savedViews: { ...previous.savedViews, [page]: views } }
    })
    setNeedsSync(true)
  }, [])

  const saveHistoryGraph = useCallback((settings: HistoryGraphSettings) => {
    setConfiguration((previous) => ({ ...previous, historyGraph: settings }))
    setNeedsSync(true)
  }, [])

  const getInsightConfiguration = useCallback(
    ({ dashboardSlug, id }: { dashboardSlug: string; id: string }) => {
      const dashboard = configuration.dashboards.find((p) => p.slug === dashboardSlug)
      if (!dashboard) {
        log.error('getInsightConfiguration: no dashboard for %s', dashboardSlug)
        throw new Error('There was an error updating the configuration. Please contact support. Reference: #AI-0')
      }
      const insight = dashboard.insights.find((i) => i.id === id)
      if (!insight) {
        log.error('getInsightConfiguration: no insight for %s/%s', dashboardSlug, id)
        throw new Error('There was an error updating the configuration. Please contact support. Reference: #AI-1')
      }
      return insight
    },
    [configuration]
  )

  const addInsight = useCallback(
    ({ insightConfiguration }: { insightConfiguration: InsightConfigurationType }) => {
      setConfiguration((previous) => {
        const dashboards = [...(previous?.dashboards || [])]
        const dashboard = dashboards.find((p) => p.slug === currentDashboardSlug)
        if (!dashboard) {
          log.error('addInsight: no dashboard for %s', currentDashboardSlug)
          throw new Error('There was an error updating the configuration. Please contact support. Reference: #AI-2')
        }
        if (!dashboard.insights) {
          dashboard.insights = []
        }
        if (dashboard.insights.some((i) => i.id === insightConfiguration.id)) {
          return previous
        }
        dashboard.insights.push(insightConfiguration)
        return { ...previous, dashboards }
      })
      setNeedsSync(true)
    },
    [currentDashboardSlug]
  )

  const removeInsight = useCallback(
    ({ id }: { id: string }) => {
      setConfiguration((previous) => {
        const dashboards = [...(previous?.dashboards || [])]
        const dashboard = dashboards.find((p) => p.slug === currentDashboardSlug)
        if (!dashboard) {
          log.error('addInsight: no dashboard for %s', currentDashboardSlug)
          throw new Error('There was an error updating the configuration. Please contact support. Reference: #RI-1')
        }
        dashboard.insights = (dashboard.insights || []).filter((i) => i.id !== id)
        return { ...previous, dashboards }
      })
      setNeedsSync(true)
    },
    [currentDashboardSlug]
  )

  const updateInsight = useCallback(
    ({ id, insightConfiguration }: { id: string; insightConfiguration: InsightConfigurationType }) => {
      setConfiguration((previous) => {
        const dashboards = previous?.dashboards || []
        const dashboardIdx = dashboards.findIndex((p) => p.slug === currentDashboardSlug)

        if (dashboardIdx < 0) {
          log.error('updateInsight: no dashboard for %s', currentDashboardSlug)
          throw new Error('There was an error updating the configuration. Please contact support. Reference: #UI-1')
        }

        const dashboard = dashboards[dashboardIdx]
        const insightIdx = (dashboard.insights || []).findIndex((i) => i.id === id)

        if (insightIdx < 0) {
          log.error('updateInsight: no insight in %s for %s', currentDashboardSlug, id)
          throw new Error('There was an error updating the configuration. Please contact support. Reference #UI-2')
        }
        // We need the object reference to change so downstream stuff can re-render
        // I'm sure I did assign to avoid that, but as of now it seems necessary.
        // Object.assign(insight, insightConfiguration)
        if (isEqual(dashboard.insights[insightIdx], insightConfiguration)) {
          return previous // No change needed
        }

        // Create new insights array with updated insight
        const updatedInsights = [...dashboard.insights]
        updatedInsights[insightIdx] = insightConfiguration

        // Create new dashboard object
        const updatedDashboard = {
          ...dashboard,
          insights: updatedInsights,
        }

        // Create new dashboards array
        const updatedDashboards = [...dashboards]
        updatedDashboards[dashboardIdx] = updatedDashboard

        return { ...previous, dashboards: updatedDashboards }
      })
      setNeedsSync(true)
    },
    [currentDashboardSlug]
  )

  const buildInsight = useCallback(
    <P, D>(insightDefinition: InsightDefinition<P, D>): InsightConfigurationType => {
      const { slug, name } = insightDefinition
      const usedSlugs: string[] = [],
        usedTitles: string[] = []
      configuration?.dashboards?.forEach((dashboard) => {
        dashboard.insights.forEach((insight) => {
          if (insight.id.startsWith(slug)) {
            usedSlugs.push(insight.id)
          }
          if (insight.title.startsWith(name)) {
            usedTitles.push(insight.title)
          }
        })
      })
      let id = slug,
        title = name
      let slugCount = 0
      while (usedSlugs.includes(id)) {
        slugCount += 1
        id = `${slug}-${slugCount}`
      }
      let titleCount = 0
      while (usedTitles.includes(title)) {
        titleCount += 1
        title = `${name} (${titleCount})`
      }
      return {
        id,
        slug,
        title,
        width: insightDefinition.defaultWidth,
        aspectRatio: insightDefinition.defaultAspectRatio,
        shared: false,
        parameters: {} as InsightParametersType,
        displayParameters: {} as InsightParametersType,
      }
    },
    [configuration]
  )

  const value = useMemo<DashboardContextType>(
    () => ({
      configuration,
      addDashboard,
      setDefaultDashboard,
      removeDashboard,
      reorderDashboards,
      getInsightConfiguration,
      addInsight,
      removeInsight,
      updateInsight,
      updateDashboardParameters,
      buildInsight,
      sync,
      saveAssetMap,
      deleteAssetMap,
      saveView,
      deleteView,
      saveHistoryGraph,
      error,
      isLoading,
      setCurrentDashboardSlug,
      currentDashboard: configuration.dashboards.find((p) => p.slug === currentDashboardSlug)!,
    }),
    [
      configuration,
      addDashboard,
      setDefaultDashboard,
      removeDashboard,
      reorderDashboards,
      getInsightConfiguration,
      addInsight,
      removeInsight,
      updateInsight,
      updateDashboardParameters,
      buildInsight,
      sync,
      saveAssetMap,
      deleteAssetMap,
      saveView,
      deleteView,
      saveHistoryGraph,
      error,
      isLoading,
      currentDashboardSlug,
      setCurrentDashboardSlug,
    ]
  )

  if (error) {
    return <Alert type="error" message={error} />
  }
  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>
}

export { DashboardContext, DashboardProvider }
export type { DashboardContextType } from './DashboardContext'
